import { autoinject, transient } from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import { bindable, computedFrom } from "aurelia-framework";
import {
    DataGridColumn,
    DataType,
    EntityMetaDataModel,
    GetQueriesResponse,
    ItemSource,
    PersistQueryResponse,
    Query,
    QueryFilter,
    SavedQueriesDataModel,
    SourceType,
    DataGridColumnHelper,
    QueryColumnHelper
} from "../../../models/queries/queries.models";
import {
    CacheClientService,
    CacheKeys,
    CacheLocation
} from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import {
    NotificationClientService,
    NotificationType
} from "../../../services/basics/notification-client.service";
import { QueriesClientService } from "../../../services/queries/queries-client.services";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { activationStrategy, Router } from "aurelia-router";
import { QueryBuilder } from "./query-builder";
import {
    AllEntityTypeConfigurationConstants,
    EntityTypeConfigurationModel,
    QueryLookupsWithContext
} from "../../../models/basics/basics.models";
import { DialogServiceConstants } from "../../../constants/basics/dialog.constants";
import { DataGrid, RowItem } from "../data-grid/data-grid";
import { QueriesEditorEvents } from "./queries-editor-events";
import { I18N } from "aurelia-i18n";
import "moment";
import { MultiQueryConfig } from "../../../widgets/models";
import { Reflection } from "../../../utilities/reflection";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { BaseClaims } from "../../../constants/authentication/base-claims";
import { UserClientService } from "../../../services/authentication/user-client.service";

@autoinject
@transient()
export class QueriesEditor {

    private _dataGrid: DataGrid;
    private _selectedSavedQuery: SavedQueriesDataModel;

    protected set datagrid(value: DataGrid) {
        this._dataGrid = value;
        if (this._dataGrid) {
            this.dispatchEvent(QueriesEditorEvents.dataGridAvailable, this._dataGrid);
        } else {
            this.dispatchEvent(QueriesEditorEvents.dataGridNotAvailable);
        }
    }

    @computedFrom(Reflection.getPropertyPath<QueriesEditor>(z => z._dataGrid))
    protected get datagrid(): DataGrid {
        return this._dataGrid;
    }

    public showSelectionFilterDropdown: boolean = false;
    public selectionFilter: { label: string, id: number } = { label: "All results", id: 1 };
    public selectionSet: { label: string, id: number }[] = [{
        id: 1,
        label: 'All results'
    },
    {
        id: 2,
        label: 'Selected results'
    },
    {
        id: 3,
        label: 'Un-selected results'
    }];

    @bindable public title: string = "";

    @bindable public showDataGrid: boolean = true;

    @bindable public showPreviewButton: boolean = false;

    @bindable public pageCount: number = 100;

    @bindable public limit: number = null;

    @bindable public selectionMode: 'none' | 'single' | 'multiple' = 'multiple';

    @bindable public queryId: number = -1;

    @bindable public enableDownloadExcel: boolean;

    @bindable public clickMode: string = "double";

    @bindable public addRoute: string;

    @bindable hideGrouping: boolean = false;

    @bindable public editRoute: string | ((item: any, router?: Router) => string);

    @bindable
    public getParams: (item: any) => any = item => ({ id: item ? item.id : -1 });

    @bindable public onRowSelected: (item: any) => string;

    @bindable public addNewText: string;

    /** Used for URLs like "odata/CampaignContacts/OtherContacts" where the entity is "CampaignContact" but a different URI needs to be specified.*/
    @bindable public baseUri?: string;

    @bindable public entityUri?: string;

    @bindable public sortAlphabetically: boolean = true;

    @bindable public fixedColumns: DataGridColumn[] = [];

    @bindable public fixedFilters: QueryFilter[] = [];

    @bindable public gridSettings: any;

    @bindable public getRowClass: (item: any, rowItem: RowItem) => string;

    @bindable public getGroupName: (columns: DataGridColumn[], item: any) => string;

    public selected = "panel-results";
    protected editColumnPropertiesButtonDisabled: boolean = true;
    public queryBuilder: QueryBuilder;
    public models: EntityMetaDataModel[];
    public selectedModel: EntityMetaDataModel;
    public selectedFilters: QueryFilter[] = [];
    public showQueryEditor: boolean;
    public saveAsText: string;
    public saveAsVisible: boolean;
    public editorAnchor: HTMLAnchorElement;
    public selectedItem: any;
    public savedQueries: Array<SavedQueriesDataModel>;
    public selectedConfigurationEntity: EntityTypeConfigurationModel;
    public activateInternalCalled: boolean = false;
    public itemSource: ItemSource;
    public canAssignQueries: boolean = false;
    public isSavingQuery: boolean = false;
    public isExecutingQuery: boolean = false;
    private metaDataLoaded = false;

    public get columns(): DataGridColumn[] {
        return this.selectedSavedQuery
            ? this.selectedSavedQuery.columns
            : this.savedQueries
                ? this.savedQueries.filter(sq => sq.queryId == -1)[0].columns
                : [];
    }

    public set columns(columns: DataGridColumn[]) {
        if (this.selectedSavedQuery) {
            this.selectedSavedQuery.columns = columns;
        } else {
            this.savedQueries.filter(sq => sq.queryId == -1)[0].columns = columns;
        }
    }

    @computedFrom(Reflection.getPropertyPath<QueriesEditor>(z => z._selectedSavedQuery))
    public get selectedSavedQuery(): SavedQueriesDataModel {
        return this._selectedSavedQuery;
    }

    public set selectedSavedQuery(value: SavedQueriesDataModel) {
        if (this._selectedSavedQuery && value &&
            this._selectedSavedQuery.queryId == value.queryId
        ) {
            return;
        }
        this._selectedSavedQuery = value;
        this.selectedSavedQueryChanged();
    }

    @computedFrom(Reflection.getPropertyPath<QueriesEditor>(z => z.selectedModel))
    public get dataGridClass() {
        return this.selectedModel ? Reflection.kebabCase(this.selectedModel.uri) : null;
    }

    @computedFrom(Reflection.getPropertyPath<QueriesEditor>(z => z.selectedModel))
    public get canEditProperties() {
        return this.selectedModel ? this.selectedModel.filterProperties.filter(x => x.isUpdatable === true).length > 0 && this.selectionMode != "none" : null;
    }

    queryIdChanged() {
        if (this.savedQueries) {
            const queriesFound = this.savedQueries.filter(
                z => z.queryId == this.queryId
            );
            this.selectedSavedQuery =
                queriesFound.length > 0 ? queriesFound[0] : null;
        } else {
            this.selectedSavedQuery = null;
        }
    }

    @computedFrom(
        Reflection.getPropertyPath<QueriesEditor>(x => x.isEditorVisible),
        Reflection.getPropertyPath<QueriesEditor>(x => x.selectedSavedQuery),
        Reflection.getPropertyPath<QueriesEditor>(x => x.selectedSavedQuery.createdByCurrentUser)
    )
    public get showDelete() {
        return this.isEditorVisible && this.selectedSavedQuery && (this.selectedSavedQuery.createdByCurrentUser || this.userClaimsService.hasPermission(BaseClaims.DeleteSharedQueries));
    }

    @computedFrom(
        Reflection.getPropertyPath<QueriesEditor>(x => x.selectedSavedQuery),
        Reflection.getPropertyPath<QueriesEditor>(x => x.selectedSavedQuery.createdByCurrentUser)
    )
    public get isReadOnly() {
        return this.selectedSavedQuery && !this.selectedSavedQuery.createdByCurrentUser && !this.userClaimsService.hasPermission(BaseClaims.EditSharedQueries) && this.selectedSavedQuery.queryId > 0;
    }

    public get isEditorVisible() {
        return (
            this.editorAnchor &&
            this.editorAnchor.classList.contains("active") &&
            this.hasSharedGloballyPermission()
        );
    }

    public constructor(private element: Element,
        private notificationService: NotificationClientService,
        private queriesService: QueriesClientService,
        private dialogClientService: DialogClientService,
        private router: Router,
        private cache: CacheClientService,
        private i18N: I18N,
        private userClaimsService: UserClaimsService,
        private userClientService: UserClientService) {
        this.queriesService.getEntityModels().then(models => {
            this.models = models;
            this.metaDataLoaded = true;
        });
        this.canAssignQueries = userClaimsService.hasPermission(BaseClaims.AssignQueries);
    }

    public addEventListener(eventName: string, func) {
        this.element.addEventListener(eventName, func);
    }

    public removeEventListener(eventName: string, func) {
        this.element.removeEventListener(eventName, func);
    }

    public getDataGrid() {
        return this.datagrid;
    }

    public determineActivationStrategy() {
        return activationStrategy.replace;
    }

    public showColumnOptions(): void {
        this.dialogClientService.show(
            DialogServiceConstants.URLs.ColumnOptionsDialog,
            {
                columns: this.columns,
                reloadData: () => this.reloadData(true),
                onSaveManageColumns: () => this.onSaveManageColumns()
            }
        );
    }

    public showAssignQueries() {
        this.dialogClientService.show(DialogServiceConstants.URLs.AssignQueriesDialog, { queryId: this.queryId });
    }

    public editProperties() {
        const selectedIds: number[] = this.datagrid.getSpecificIds();
        const filters: number[] = this.datagrid.allFilters;

        if ((selectedIds.length != 0 && !this.datagrid.toggleAll) || this.datagrid.toggleAll) {
            this.dialogClientService.show(DialogServiceConstants.URLs.EditPropertiesDialog, {
                model: this.selectedModel,
                selectedIds: selectedIds,
                filters: filters,
                reload: () => this.getDataGrid().reloadData(),
                toggleAll: this.datagrid.toggleAll
            });
        }
        else {
            this.notificationService.show(this.i18N.tr('no-record-selected'), this.i18N.tr('no-record-selected-description'), NotificationType.Warning);
        }
    }

    public onSaveManageColumns = (): void => {
        this.dispatchEvent(QueriesEditorEvents.saveManageColumns);
    }

    public navigateToAddRoute() {
        const route = Enumerable.from(this.router.routes).firstOrDefault(
            x => x.name == this.addRoute
        );
        const routeName = route
            ? Array.isArray(route.route)
                ? route.route[0]
                : route.route
            : null;
        if (this.router.hasRoute(routeName)) {
            this.router.navigate(routeName);
        } else if (route && route.name) {
            this.router.navigateToRoute(
                route.name,
                this.getParams ? this.getParams(null) : null
            );
        } else if (this.addRoute) {
            this.router.navigateToRoute(this.addRoute, this.getParams(null));
        }
        this.dispatchEvent(QueriesEditorEvents.createEntity);
    }

    public entityUriChanged() {
        this.bind();
    }

    public bind(): void {
        if (this.activateInternalCalled || !this.entityUri) {
            return;
        }
        this.loadEntityModels(() => {
            this.cache
                .get(CacheKeys.selectedFilters, CacheLocation.SessionStorage)
                .then(queryConfig => {
                    if (queryConfig) {
                        const config = <MultiQueryConfig>queryConfig;
                        config.queryFilters.forEach(x => {
                            this.selectedFilters.push(x)
                        });
                        this.columns = [];
                        config.columns.forEach(x =>
                            this.columns.push(x)
                        );

                        this.cache.remove(
                            CacheKeys.selectedFilters,
                            CacheLocation.SessionStorage
                        );
                        setTimeout(() => this.reloadData());
                    } else {
                        setTimeout(() => {
                            if (!this.queryId || this.queryId <= 0) {
                                this.newQuery();
                            } else {
                                const queriesFound = this.savedQueries.filter(
                                    z => z.queryId == this.queryId
                                );
                                this.selectedSavedQuery =
                                    queriesFound.length > 0 ? queriesFound[0] : null;
                                if (!this.selectedSavedQuery) {
                                    this.newQuery();
                                }
                            }
                        });
                    }
                });
        });
    }

    public activateInternal(selected: string, func: () => void) {
        this.activateInternalCalled = true;

        this.initQueries();

        for (let i = 1; i <= this.selectedFilters.length; i++) {
            this.selectedFilters.splice(0, 1);
        }

        this.entityUri = selected;

        this.loadEntityModels(() => {
            setTimeout(() => {
                this.newQuery(true);

                if (func) {
                    func();
                }
            });
        });
    }

    public dispatchDataGridReload() {
        this.dispatchEvent(QueriesEditorEvents.dataGridReload, null);
    }

    public onReloadEnded() {
        this.isExecutingQuery = false;
    }

    public selectedModelLoaded(func: () => void): void {

        const columns: DataGridColumn[] = [];

        for (const item of this.selectedModel.projectionProperties) {
            const column: DataGridColumn = QueryColumnHelper.generateDefaultColumn(item, this.selectedModel);
            columns.push(column);
            this.dispatchEvent(QueriesEditorEvents.columnPushed, column);
        }

        this.dispatchEvent(QueriesEditorEvents.columnsPrepared, columns);
        this.columns = columns;

        if (this.queryId <= 0) {
            this.selectedFilters.forEach((filter) => {
                if (filter.subscription) {
                    filter.subscription.dispose();
                }
            });

            this.selectedFilters.splice(0, this.selectedFilters.length);
        }

        setTimeout(() => {
            if (this.queryBuilder) {
                this.queryBuilder.reComputeIsLast();
            }
            if (func) {
                func();
            }
        });
    }

    public getColumnsFromQuery(query: SavedQueriesDataModel) {
        const columns: DataGridColumn[] = this.fixedColumns ? Enumerable.from(this.fixedColumns).toArray() : [];

        for (const item of this.selectedModel.projectionProperties) {
            const column: DataGridColumn = QueryColumnHelper.generateDefaultColumn(item, this.selectedModel);
            columns.push(column);
            this.dispatchEvent(QueriesEditorEvents.columnPushed, column);
        }

        const savedColumns = query.columns.concat(
            this.fixedColumns ? Enumerable.from(this.fixedColumns).toArray() : []
        );

        for (const x of columns) {
            const currentColumn = Enumerable.from(savedColumns).firstOrDefault(z => z.name === x.name);

            //Hide column if it wasnt part of the saved query
            x.isHidden = currentColumn ? currentColumn.isHidden : true;
            x.sortOrder = currentColumn ? currentColumn.sortOrder : x.sortOrder;
            x.groupOrder = currentColumn ? currentColumn.groupOrder : x.groupOrder;
            x.isSortable = currentColumn ? currentColumn.isSortable : x.isSortable;
            x.isGrouping = currentColumn ? currentColumn.isGrouping : x.isGrouping;
            x.columnOrder = currentColumn ? currentColumn.columnOrder : x.columnOrder;
            x.isAscending = currentColumn ? currentColumn.isAscending : x.isAscending;
        }

        this.dispatchEvent(QueriesEditorEvents.columnsPrepared, columns);
        return columns;
    }

    public reloadData = (force: boolean = false): void => {
        if (!this.selectedModel) return;
        const uri = !(this.baseUri && this.baseUri.trim().length > 0)
            ? "odata/" + this.selectedModel.uri + (location.href.split("?$filter").length > 1
                ? "?" + location.href.split("?")[1]
                : "")
            : this.baseUri;
        let filters = this.selectedFilters
            .filter(x => x.filterProperty && x.filterOperator)
            .concat(this.fixedFilters ? this.fixedFilters : []);
        if (filters.length == 0) {
            filters = [];
        }
        const query = this._selectedSavedQuery
            ? this._selectedSavedQuery
            : this.savedQueries.filter(sq => sq.queryId == -1)[0];

        const columns = this.getColumnsFromQuery(query);

        const itemSource = {
            source: uri,
            sourceType: SourceType.ODataUrl,
            getGroupName: this.getGroupName,
            columns,
            initialFilters: filters,
            propertyDefaults: this.gridSettings,
            limit: this.limit,
            getRowClass: this.getRowClass
        };
        if (!this.areEqualSources(this.itemSource, itemSource) || force) {
            if (
                this.selectedConfigurationEntity &&
                this.selectedConfigurationEntity.columnOverrides
            ) {
                columns.forEach(col => {
                    const columnOverride = this.selectedConfigurationEntity.columnOverrides.filter(
                        x => x.property == col.name
                    )[0];
                    if (columnOverride) {
                        DataGridColumnHelper.applyDataGridColumnOverride(col, columnOverride);
                    }
                });
            }

            this.dispatchEvent(QueriesEditorEvents.itemSourcePrepared, itemSource);

            this.itemSource = itemSource;
        }
    }

    areEqualSources(source1: ItemSource, source2: ItemSource) {
        if (source2 != source1 && (!source1 || !source2)) {
            return false;
        }
        const replacer = (k, v) => {
            if (k === "filters") {
                return undefined;
            }
            return v;
        };
        const areEqualUrl = source1.source == source2.source;
        const areEqualSourceType = source1.sourceType == source2.sourceType;
        const areEqualInitialFilters =
            JSON.stringify(source1.initialFilters) ==
            JSON.stringify(source2.initialFilters);
        const areEqualColumns =
            JSON.stringify(source1.columns, replacer) ==
            JSON.stringify(source2.columns, replacer);
        return (
            areEqualUrl &&
            areEqualSourceType &&
            areEqualInitialFilters &&
            areEqualColumns
        );
    }

    public showSaveAs(): void {
        this.saveAsVisible = true;
    }

    public cancelSaveAs(): void {
        this.saveAsVisible = false;
        this.saveAsText = "";
    }

    public select(item: string) {
        this.selected = item;
    }

    public newQuery(skipQueryChangedEventDispatch: boolean = false): void {
        this.queryId = null;
        const queryFilters = [];
        const columns = [];

        this.selectedModel.projectionProperties.forEach(item => {
            const column: DataGridColumn = QueryColumnHelper.generateDefaultColumn(item, this.selectedModel);

            if ((item.type === DataType.Date || item.type === DataType.DateTime) && !column.valueConverter) {
                column.valueConverter = QueryColumnHelper.getDateFilterFunc(item);
            }
            columns.push(column);

            this.dispatchEvent(QueriesEditorEvents.columnPushed, column);

        });
        this.dispatchEvent(QueriesEditorEvents.columnsPrepared, columns);

        this.columns = columns;
        this.selectedFilters = queryFilters;
        setTimeout(() => {
            if (
                this.queryBuilder &&
                (!this.queryBuilder.clauses || this.queryBuilder.clauses.length == 0)
            ) {
                this.queryBuilder.addFilter(null);
            }
            this.reloadData();
            if (!skipQueryChangedEventDispatch) {
                this.dispatchEvent('query-changed', null);
            }
        });

    }

    public saveAs(): Promise<PersistQueryResponse> {
        this.saveAsVisible = false;
        const qf: QueryFilter[] = [];

        for (const x of this.selectedFilters) {
            qf.push(CloneHelper.clone(x));

        }
        const query = new SavedQueriesDataModel(this.saveAsText,
            qf,
            0,
            new Date(),
            this.itemSource.columns,
            true);

        if (!this.savedQueries) {
            this.initQueries();
        }
        this.saveAsText = "";
        this.savedQueries.push(query);
        return this.persistQuery(query);
    }

    public initQueries() {
        this.savedQueries = [
            <SavedQueriesDataModel>{
                queryId: -1,
                name: " "
            }
        ];
    }

    public selectedSavedQueryChanged() {
        const selectedSavedQuery = this.selectedSavedQuery;
        if (selectedSavedQuery) {
            this.dispatchEvent(QueriesEditorEvents.savedQueryChanged, {
                queryId: selectedSavedQuery.queryId
            });
        }
        if (selectedSavedQuery && selectedSavedQuery.queryId !== -1) {
            this.selectedFilters.forEach((filter) => {
                if (filter.subscription) {
                    filter.subscription.dispose();
                }
            });
            this.selectedFilters.splice(0, this.selectedFilters.length);
            for (const x of selectedSavedQuery.queryFilters) {
                this.selectedFilters.push(QueryFilter.clone(x));
            }

            if (this.selectedModel) {

                const columns: DataGridColumn[] = [];

                for (const item of this.selectedModel.projectionProperties) {
                    const column: DataGridColumn = QueryColumnHelper.generateDefaultColumn(item, this.selectedModel);
                    columns.push(column);
                    this.dispatchEvent(QueriesEditorEvents.columnPushed, column);
                }

                const savedColumns = Enumerable.from(selectedSavedQuery.columns);

                for (const x of columns) {
                    const currentColumn = savedColumns.firstOrDefault(z => z.name === x.name);

                    x.isHidden = currentColumn ? currentColumn.isHidden : true;
                    x.sortOrder = currentColumn ? currentColumn.sortOrder : x.sortOrder;
                    x.groupOrder = currentColumn ? currentColumn.groupOrder : x.groupOrder
                    x.isSortable = currentColumn ? currentColumn.isSortable : x.isSortable
                    x.isGrouping = currentColumn ? currentColumn.isGrouping : x.isGrouping
                    x.columnOrder = currentColumn ? currentColumn.columnOrder : x.columnOrder
                    x.isAscending = currentColumn ? currentColumn.isAscending : x.isAscending
                    x.isDynamicProperty = currentColumn ? currentColumn.isDynamicProperty : x.isDynamicProperty
                    x.isCollection = currentColumn ? currentColumn.isCollection : x.isCollection
                    const property = this.selectedModel.projectionProperties.filter(prop => prop.name == x.name)[0];
                    if (property && (property.type === DataType.Date || property.type === DataType.DateTime) && !x.valueConverter) {
                        x.valueConverter = QueryColumnHelper.getDateFilterFunc(property);
                    }
                    if (this.selectedConfigurationEntity && this.selectedConfigurationEntity.columnOverrides && currentColumn) {
                        const columnOverride = this.selectedConfigurationEntity.columnOverrides.filter(x => x.property == currentColumn.name)[0];
                        if (columnOverride) {
                            DataGridColumnHelper.applyDataGridColumnOverride(currentColumn, columnOverride);
                        }
                    }
                }

                this.dispatchEvent(QueriesEditorEvents.columnsPrepared, columns);
                this.columns = columns;
                this.reloadData();
            }
        } else if (selectedSavedQuery && selectedSavedQuery.queryId === -1) {
            this.newQuery();
        }

        this.dispatchEvent('query-changed', this.selectedSavedQuery);
    }

    runQuery() {
        this.isExecutingQuery = true;
        if (this.showPreviewButton) {
            this.preview();
        } else {
            this.reloadData(true);
        }
    }

    preview() {
        this.reloadData(true);
        this.showDataGrid = true;
    }

    public onItemSelected(event) {
        if (event.detail.item) {
            if ((this.datagrid.getSpecificIds().length != 0 && !this.datagrid.toggleAll) || this.datagrid.toggleAll) {
                this.editColumnPropertiesButtonDisabled = false;
            }

            if (!this.datagrid.toggleAll && this.datagrid.getSpecificIds().length == 0) {
                this.editColumnPropertiesButtonDisabled = true;
            }

            this.selectedItem = event.detail.item;
            if (this.clickMode == "single") {
                this.doubleClick(event, true);
            }
        }
    }

    public selectedModelChanged($event) {
        this.selectedModel = $event.detail;
    }

    public doubleClick(event, raisedManually) {
        if (
            (raisedManually && this.clickMode == "single") ||
            this.clickMode == "double"
        ) {
            if (!event.detail.item) {
                return;
            }
            this.dispatchEvent(QueriesEditorEvents.rowSelected, event.detail);
            this.selectedItem = event.detail.item;
            const route = this.onRowSelected
                ? this.onRowSelected(this.selectedItem)
                : this.editRoute
                    ? this.editRoute
                    : null;
            if (typeof route == "string") {
                const params = this.getParams(this.selectedItem);
                this.router.navigateToRoute(route, params);
            } else if (typeof route == "function") {
                route(this.selectedItem, this.router);
            }
        }
    }

    public save(): Promise<PersistQueryResponse> {
        const selectedSavedQuery = this.selectedSavedQuery;
        const qf = new Array<QueryFilter>();

        for (const x of this.selectedFilters) {
            qf.push(CloneHelper.clone(x));
        }

        if (selectedSavedQuery) {
            selectedSavedQuery.queryFilters = qf;
            selectedSavedQuery.columns = this.itemSource.columns;

            return this.persistQuery(selectedSavedQuery);
        } else {
            return this.saveAs();
        }
    }

    public showDeleteDialog() {
        this.dialogClientService.show(DialogServiceConstants.URLs.DeleteModal, this);
    }

    public delete() {
        const selectedSavedQuery = this.selectedSavedQuery;
        if (selectedSavedQuery) {
            this.queriesService.deleteQuery(selectedSavedQuery.queryId)
                .then(x => {
                    this.dialogClientService.hide();

                    if (!x.isSuccess) {
                        this.notificationService.show(this.i18N.tr('query-not-found-text'), "", NotificationType.Warning);
                        return;
                    }

                    this.notificationService.show(this.i18N.tr('success-text'), this.i18N.tr('successful-deletion-query-editor'), NotificationType.Success);
                    const index = this.savedQueries.indexOf(selectedSavedQuery);

                    this.savedQueries.splice(index, 1);
                    if (this.queryId) {
                        this.queryId = null;
                    } else {
                        this.queryId = -1;
                    }
                    this.newQuery();

                    this.dispatchEvent(QueriesEditorEvents.savedQueryChanged, {
                        queryId: -1
                    });
                })
                .catch(x => {
                    if (!x) {
                        this.notificationService.show(this.i18N.tr('error-text'), this.i18N.tr('error-message-delete-query'), NotificationType.Error);
                    }
                });
        }
    }

    protected persistQuery(queryToSave: SavedQueriesDataModel): Promise<PersistQueryResponse> {
        return new Promise<PersistQueryResponse>((resolve, reject) => {
            this.isSavingQuery = true;
            let query: Query = null;

            for (const item of queryToSave.queryFilters) {
                const contextProperty = QueryLookupsWithContext.filter(x => item.filterProperty && item.filterProperty.lookupMappedToProperty && x.lookup.toLowerCase() == item.filterProperty.lookupMappedToProperty.toLowerCase())[0];
                if (contextProperty) {
                    item.filterContextPropertyValue = this.userClientService.currentUser[contextProperty.contextProperty];
                }
            }

            if (queryToSave) {
                query = new Query(
                    queryToSave.queryId,
                    this.selectedModel.uri,
                    queryToSave.name,
                    queryToSave.dateCreated,
                    JSON.stringify(queryToSave.queryFilters),
                    JSON.stringify(queryToSave.columns),
                    "",
                    queryToSave.createdByCurrentUser
                );
            }

            this.queriesService.persistQueries(query, null, null).then(
                (result: PersistQueryResponse) => {
                    const queryToUpdate = this.savedQueries.filter(x => x.queryId === 0)[0];
                    if (queryToUpdate) {
                        queryToUpdate.queryId = result.savedQueryId;
                        this.queryId = queryToUpdate.queryId;
                    }

                    if (query) {
                        this.notificationService.show(this.i18N.tr('success-text'), this.i18N.tr('successful-saving-manage-queries'), NotificationType.Success);
                    }

                    this.isSavingQuery = false;
                    resolve(null);
                }
            ).catch(x => {
                if (!x) {
                    this.notificationService.show(this.i18N.tr('error-text'), this.i18N.tr('error-message-delete-query'), NotificationType.Error);
                }

                this.isSavingQuery = false;
                reject();
            });
        });
    }

    public hasSharedGloballyPermission() {
        return this.userClaimsService.hasPermission(BaseClaims.ShareGlobally);
    }

    protected loadSavedQueries() {
        return new Promise<void>((resolve, reject) => {
            if (!this.selectedModel || !this.selectedModel.uri) {
                resolve();
            }
            const promise: Promise<GetQueriesResponse> = this.queriesService.getQueriesForUser(this.selectedModel.uri);

            promise.then(
                (result: GetQueriesResponse) => {
                    const queries = result.queries;
                    if (!this.savedQueries) {
                        this.initQueries();
                    }
                    const queriesList = [
                        <SavedQueriesDataModel>{
                            queryId: -1,
                            name: " ",
                            columns: []
                        }
                    ];

                    if (this.models) {
                        for (const query of queries) {
                            const qf: QueryFilter[] = JSON.parse(query.queryBody);

                            queriesList.push(
                                new SavedQueriesDataModel(
                                    query.description,
                                    qf,
                                    (<Query>query).queryId,
                                    query.createdDate,
                                    JSON.parse(query.columns),
                                    query.createdByCurrentUser
                                )
                            );
                        }
                    }
                    if (this.queryId == -1) this.queryId = result.defaultQueryId
                    this.savedQueries = queriesList;

                    const isQueryIdInList = Enumerable.from(queriesList).any(x => x.queryId == this.queryId);

                    if (this.queryId == -1 || !isQueryIdInList) {
                        this.queryId = result.defaultQueryId;
                    }
                    resolve();
                },
                error => {
                    reject(error);
                }
            );
        });
    }

    public pageChanged = () => {
        this.dispatchEvent('page-changed');
    }

    protected loadEntityModels(func: () => void) {
        const action = () => {
            //load model before lodaing queries to use uri since the type and uri is different on some
            this.selectedModel = Enumerable.from(this.models).firstOrDefault(y => y.uri && y.uri.toLowerCase() == this.entityUri.toLowerCase())
            this.selectedConfigurationEntity = AllEntityTypeConfigurationConstants.filter(
                x =>
                    x.uri &&
                    this.selectedModel &&
                    this.selectedModel.uri &&
                    x.uri.replace(/\s/g, "").toLowerCase() ==
                    this.selectedModel.uri.replace(/\s/g, "").toLowerCase()
            )[0];

            this.loadSavedQueries().then(() => {
                this.selectedModel = Enumerable.from(this.models).firstOrDefault(y => y.uri && y.uri.toLowerCase() === this.entityUri.toLowerCase())
                this.selectedConfigurationEntity = AllEntityTypeConfigurationConstants.filter(
                    x =>
                        x.uri &&
                        this.selectedModel &&
                        this.selectedModel.uri &&
                        x.uri.replace(/\s/g, "").toLowerCase() ==
                        this.selectedModel.uri.replace(/\s/g, "").toLowerCase()
                )[0];
                this.selectedModelLoaded(func);
                const query = this.savedQueries.filter(sq => sq.queryId == -1)[0];
                query.columns = [];
                this.selectedModel.projectionProperties.forEach(p => {
                    const column = QueryColumnHelper.generateDefaultColumn(p, this.selectedModel);
                    query.columns.push(column);
                    this.dispatchEvent(QueriesEditorEvents.columnPushed, column);
                });
            });

        };

        if (this.metaDataLoaded) {
            action();
        } else {
            this.queriesService.getEntityModels().then((models: EntityMetaDataModel[]) => {
                this.models = models;
                action();
            },  () => {
                this.notificationService.show(
                    this.i18N.tr("error-text") + "!",
                    this.i18N.tr("error-loading-meta-data-for-query-editor-text"),
                    NotificationType.Error
                );
            }
            );
        }
    }

    private dispatchEvent(nameEvent: string, data?: any) {
        if (this.element && nameEvent) {
            const customEvent: CustomEvent = new CustomEvent(nameEvent, {
                bubbles: true,
                detail: data
            });
            this.element.dispatchEvent(customEvent);
        }
    }

    selectFilterItem(filterItem: { label: string, id: number }) {
        this.selectionFilter = filterItem;
        this.showSelectionFilterDropdown = false;
    }

    toggleSelectionFilterDropdown(e) {
        e.stopPropagation();
        this.showSelectionFilterDropdown = !this.showSelectionFilterDropdown;
    }
}
